import React, { useEffect, useMemo, useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader, Spinner, Card, PrimaryButton, GhostButton } from "@/components/common";
import { useAuth } from "@/context/AuthContext";
import {
  Database, ShieldCheck, Eye, Archive, Trash2, AlertTriangle,
  CheckCircle2, RotateCcw, Loader2, Lock,
} from "lucide-react";

export default function DemoData() {
  const { hasRole } = useAuth();
  const isAdmin = hasRole("admin");
  const [audit, setAudit] = useState(null);
  const [selected, setSelected] = useState({}); // cat -> action
  const [preview, setPreview] = useState(null);
  const [backup, setBackup] = useState(null);
  const [confirmBackup, setConfirmBackup] = useState(false);
  const [confirmFinal, setConfirmFinal] = useState(false);
  const [report, setReport] = useState(null);
  const [archive, setArchive] = useState([]);
  const [busy, setBusy] = useState("");

  const loadAudit = () => api.get("/admin/demo-data/audit").then((r) => setAudit(r.data)).catch((e) => toast.error(apiError(e)));
  const loadArchive = () => api.get("/admin/demo-data/archive").then((r) => setArchive(r.data)).catch(() => {});
  useEffect(() => { loadAudit(); loadArchive(); }, []);

  const selectedCats = useMemo(() => Object.keys(selected), [selected]);
  const resetDownstream = () => { setPreview(null); setBackup(null); setConfirmBackup(false); setConfirmFinal(false); setReport(null); };

  const toggle = (cat) => {
    setSelected((s) => {
      const n = { ...s };
      if (n[cat]) delete n[cat]; else n[cat] = "archive";
      return n;
    });
    resetDownstream();
  };
  const setAction = (cat, action) => { setSelected((s) => ({ ...s, [cat]: action })); resetDownstream(); };

  const runPreview = async () => {
    setBusy("preview");
    try {
      const { data } = await api.post("/admin/demo-data/preview", { categories: selectedCats });
      setPreview(data); setBackup(null); setConfirmBackup(false); setConfirmFinal(false); setReport(null);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };

  const runBackup = async () => {
    setBusy("backup");
    try {
      const { data } = await api.post("/admin/demo-data/backup", { categories: selectedCats });
      setBackup(data); toast.success(`Backup created - ${data.total_records} records snapshotted`);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };

  const runCleanup = async () => {
    setBusy("cleanup");
    try {
      const selections = selectedCats.map((c) => ({ category: c, action: selected[c] }));
      const { data } = await api.post("/admin/demo-data/cleanup", { selections, backup_id: backup.backup_id, confirm: true });
      setReport(data); toast.success("Cleanup complete");
      setSelected({}); setPreview(null); setBackup(null); setConfirmBackup(false); setConfirmFinal(false);
      loadAudit(); loadArchive();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };

  const restore = async (aid) => {
    try { await api.post(`/admin/demo-data/archive/${aid}/restore`); toast.success("Record restored"); loadAudit(); loadArchive(); }
    catch (e) { toast.error(apiError(e)); }
  };

  if (!audit) return <Spinner />;
  const t = audit.totals || {};
  const canCleanup = isAdmin && selectedCats.length > 0 && preview && backup && confirmBackup && confirmFinal;

  return (
    <div className="animate-fadeUp" data-testid="demo-data-page">
      <PageHeader title="Demo Data Management"
        subtitle="Audit sample/demo data and remove it safely before go-live. Nothing is deleted without preview, backup and your explicit confirmation." />

      {/* Summary */}
      <div className="grid gap-4 sm:grid-cols-3">
        <Card className="border-amber-300"><div className="flex items-center gap-2 text-amber-600"><AlertTriangle className="h-5 w-5" /><p className="text-sm font-medium">Confirmed demo records</p></div><p className="mt-2 font-display text-4xl font-bold" data-testid="total-demo">{t.demo ?? 0}</p></Card>
        <Card className="border-sky-300"><div className="flex items-center gap-2 text-sky-600"><Eye className="h-5 w-5" /><p className="text-sm font-medium">Needs manual review</p></div><p className="mt-2 font-display text-4xl font-bold" data-testid="total-review">{t.review ?? 0}</p></Card>
        <Card className="border-emerald-300"><div className="flex items-center gap-2 text-emerald-600"><ShieldCheck className="h-5 w-5" /><p className="text-sm font-medium">Real - preserved</p></div><p className="mt-2 font-display text-4xl font-bold" data-testid="total-real">{t.real ?? 0}</p></Card>
      </div>

      {audit.demo_seed_disabled && (
        <p className="mt-3 flex items-center gap-2 text-xs text-muted-foreground"><Lock className="h-3.5 w-3.5" /> Automatic demo-seeding is disabled for this environment (demo data was previously cleaned).</p>
      )}

      {/* Audit table */}
      <Card className="mt-6 overflow-x-auto p-0">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border bg-background/40 text-left font-mono text-xs uppercase tracking-wide text-muted-foreground">
              <th className="px-4 py-3 font-medium">Select</th>
              <th className="px-4 py-3 font-medium">Entity</th>
              <th className="px-4 py-3 font-medium text-right">Total</th>
              <th className="px-4 py-3 font-medium text-right">Demo</th>
              <th className="px-4 py-3 font-medium text-right">Review</th>
              <th className="px-4 py-3 font-medium text-right">Real</th>
              <th className="px-4 py-3 font-medium">Action</th>
            </tr>
          </thead>
          <tbody>
            {audit.entities.map((e) => (
              <tr key={e.category} className="border-b border-border last:border-0" data-testid={`audit-row-${e.category}`}>
                <td className="px-4 py-2.5">
                  {e.cleanable && e.demo > 0 ? (
                    <input type="checkbox" data-testid={`cat-check-${e.category}`} checked={!!selected[e.category]} onChange={() => toggle(e.category)} className="h-4 w-4 accent-primary" />
                  ) : <span className="text-muted-foreground">-</span>}
                </td>
                <td className="px-4 py-2.5">
                  <span className="font-medium">{e.label}</span>
                  {e.protected && <span className="ml-2 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-600">protected</span>}
                </td>
                <td className="px-4 py-2.5 text-right font-mono">{e.total}</td>
                <td className="px-4 py-2.5 text-right font-mono text-amber-600">{e.demo || ""}</td>
                <td className="px-4 py-2.5 text-right font-mono text-sky-600">{e.review || ""}</td>
                <td className="px-4 py-2.5 text-right font-mono text-emerald-600">{e.real || ""}</td>
                <td className="px-4 py-2.5">
                  {selected[e.category] && (
                    <select data-testid={`cat-action-${e.category}`} value={selected[e.category]} onChange={(ev) => setAction(e.category, ev.target.value)}
                      className="rounded-lg border border-input bg-background px-2 py-1.5 text-xs outline-none focus:border-primary">
                      <option value="archive">Archive (reversible)</option>
                      <option value="delete">Delete permanently</option>
                    </select>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      {/* Step 1: preview */}
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <PrimaryButton data-testid="preview-btn" disabled={selectedCats.length === 0 || busy} onClick={runPreview}>
          {busy === "preview" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Eye className="h-4 w-4" />} Preview (dry run)
        </PrimaryButton>
        <span className="text-sm text-muted-foreground">{selectedCats.length} categor{selectedCats.length === 1 ? "y" : "ies"} selected</span>
      </div>

      {preview && (
        <Card className="mt-4" >
          <h3 className="font-display text-lg font-semibold">Dry-run preview - no data has been changed</h3>
          {preview.selections.every((s) => s.count === 0) && <p className="mt-2 text-sm text-muted-foreground">No demo records found for the selected categories.</p>}
          {preview.selections.map((s) => (
            <div key={s.category} className="mt-4" data-testid={`preview-${s.category}`}>
              <p className="text-sm font-semibold capitalize">{s.category.replace(/_/g, " ")} - {s.removable} removable{s.blocked ? `, ${s.blocked} blocked` : ""}</p>
              <div className="mt-2 space-y-2">
                {s.records.map((r) => (
                  <div key={r.id} className="rounded-lg border border-border bg-background/40 p-3">
                    <div className="flex flex-wrap items-center gap-2">
                      {r.blocked ? <AlertTriangle className="h-4 w-4 text-amber-600" /> : <Trash2 className="h-4 w-4 text-muted-foreground" />}
                      <span className="font-medium">{r.label}</span>
                      <span className="font-mono text-xs text-muted-foreground">{r.id}</span>
                      <span className="rounded-full bg-secondary px-2 py-0.5 text-[10px] text-muted-foreground">{r.reason}</span>
                      {r.blocked && <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-semibold text-amber-600">blocked - genuine links; manual review</span>}
                    </div>
                    {r.dependents.length > 0 && (
                      <p className="mt-1.5 pl-6 text-xs text-muted-foreground">
                        Linked: {r.dependents.map((d) => `${d.category}:${d.label}${d.is_demo ? "" : " (genuine)"}`).join(", ")}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </Card>
      )}

      {/* Step 2+3: backup & confirm (admin only) */}
      {preview && isAdmin && preview.selections.some((s) => s.removable > 0) && (
        <Card className="mt-4 border-destructive/40">
          <h3 className="font-display text-lg font-semibold">Back up & confirm</h3>
          <p className="mt-1 text-sm text-muted-foreground">A backup snapshot is required before any removal. Archived records can be restored below.</p>
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <GhostButton data-testid="backup-btn" disabled={busy} onClick={runBackup}>
              {busy === "backup" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Database className="h-4 w-4" />} Create backup snapshot
            </GhostButton>
            {backup && <span className="flex items-center gap-1.5 text-sm text-emerald-600"><CheckCircle2 className="h-4 w-4" /> Backup {backup.backup_id.slice(0, 8)} ({backup.total_records} records)</span>}
          </div>
          <div className="mt-4 space-y-2.5">
            <label className="flex items-start gap-2 text-sm">
              <input type="checkbox" data-testid="confirm-backup-check" disabled={!backup} checked={confirmBackup} onChange={(e) => setConfirmBackup(e.target.checked)} className="mt-0.5 h-4 w-4 accent-primary" />
              <span>I have verified a database backup / recovery point is in place.</span>
            </label>
            <label className="flex items-start gap-2 text-sm">
              <input type="checkbox" data-testid="confirm-final-check" disabled={!backup} checked={confirmFinal} onChange={(e) => setConfirmFinal(e.target.checked)} className="mt-0.5 h-4 w-4 accent-primary" />
              <span>I understand only the <b>confirmed demo</b> records above will be affected, and I authorise this cleanup.</span>
            </label>
          </div>
          <button data-testid="run-cleanup-btn" disabled={!canCleanup || busy} onClick={runCleanup}
            className="mt-4 inline-flex items-center gap-2 rounded-lg bg-destructive px-4 py-2.5 text-sm font-semibold text-white transition-all hover:opacity-90 disabled:opacity-40">
            {busy === "cleanup" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />} Run cleanup
          </button>
        </Card>
      )}

      {!isAdmin && <p className="mt-4 text-sm text-muted-foreground">You can review the audit and preview. Only an administrator can back up and run cleanup.</p>}

      {report && (
        <Card className="mt-4 border-emerald-300" data-testid="cleanup-report">
          <h3 className="font-display text-lg font-semibold text-emerald-600">Cleanup report</h3>
          <div className="mt-3 grid gap-3 sm:grid-cols-2 text-sm">
            <div><p className="font-semibold">Removed</p><pre className="mt-1 whitespace-pre-wrap text-xs text-muted-foreground">{JSON.stringify(report.removed, null, 2)}</pre></div>
            <div><p className="font-semibold">Archived</p><pre className="mt-1 whitespace-pre-wrap text-xs text-muted-foreground">{JSON.stringify(report.archived, null, 2)}</pre></div>
          </div>
          {report.skipped?.length > 0 && (
            <div className="mt-3 text-sm"><p className="font-semibold text-amber-600">Skipped / flagged for review</p>
              {report.skipped.map((s, i) => <p key={i} className="text-xs text-muted-foreground">{s.category} {s.label || ""} - {s.reason}</p>)}
            </div>
          )}
          <p className="mt-3 text-xs text-muted-foreground">{report.preserved_note}</p>
        </Card>
      )}

      {/* Archive + restore */}
      {archive.length > 0 && (
        <Card className="mt-6">
          <div className="mb-3 flex items-center gap-2"><Archive className="h-5 w-5 text-primary" /><h3 className="font-display text-lg font-semibold">Archived demo records</h3></div>
          <div className="space-y-2">
            {archive.map((a) => (
              <div key={a.id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-border bg-background/40 p-3" data-testid={`archive-${a.id}`}>
                <div className="text-sm"><span className="font-medium">{a.label}</span> <span className="font-mono text-xs text-muted-foreground">{a.source_collection}</span></div>
                {isAdmin && (
                  <button data-testid={`restore-${a.id}`} onClick={() => restore(a.id)} className="inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-xs font-semibold hover:border-primary/50 hover:text-primary">
                    <RotateCcw className="h-3.5 w-3.5" /> Restore
                  </button>
                )}
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
